import { useState, type FormEvent } from 'react';
import { collection, deleteDoc, doc, getDocs, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { ShieldCheck, Sparkles, Trash2, UserPlus } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAuth } from '../../context/AuthContext';
import { useAdminCollection } from './hooks';
import { EmptyState, Field } from './adminUi';
import { ADMIN_EMAILS } from '../../lib/firebase-config';
import type { StaffMember, StaffRole } from '../../lib/types';

export default function TeamAdmin() {
  const { user } = useAuth();
  const { items, loading } = useAdminCollection<StaffMember>('admins');
  const [form, setForm] = useState({ email: '', role: 'cleaner' as StaffRole });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const addStaff = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setBusy(true);
    try {
      const email = form.email.trim().toLowerCase();
      const snap = await getDocs(query(collection(db(), 'users'), where('email', '==', email)));
      if (snap.empty) {
        setError(
          `No account found for ${email}. Ask them to create an account first at apexcarcare.net/login — then add them here.`,
        );
        return;
      }
      const userDoc = snap.docs[0];
      const data = userDoc.data() as { name?: string };
      await setDoc(doc(db(), 'admins', userDoc.id), {
        role: form.role,
        email,
        name: data.name ?? '',
        addedAt: Date.now(),
      });
      setSuccess(`${data.name || email} added as ${form.role}.`);
      setForm({ email: '', role: 'cleaner' });
    } catch (err: any) {
      setError(err?.message ?? 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m: StaffMember) => {
    if (m.id === user?.uid) {
      alert("You can't remove yourself.");
      return;
    }
    if (confirm(`Remove ${m.name || m.email} from the team? They'll lose staff access immediately.`)) {
      await deleteDoc(doc(db(), 'admins', m.id));
    }
  };

  const setRole = (m: StaffMember, role: StaffRole) => updateDoc(doc(db(), 'admins', m.id), { role });

  return (
    <div className="mx-auto max-w-4xl">
      <h1 className="font-display text-2xl font-bold text-white">Team</h1>
      <p className="mt-1 text-sm text-zinc-500">
        <span className="font-semibold text-zinc-300">Admins</span> can manage everything.{' '}
        <span className="font-semibold text-zinc-300">Cleaners</span> only see and work bookings —
        perfect for detailers who need the day's schedule.
      </p>

      <form onSubmit={addStaff} className="card mt-6 grid gap-4 p-6 sm:grid-cols-[1fr_170px_auto]">
        <Field label="Team member's account email">
          <input
            className="input"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="cleaner@email.com"
          />
        </Field>
        <Field label="Role">
          <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as StaffRole })}>
            <option value="cleaner">Cleaner — bookings only</option>
            <option value="admin">Admin — full access</option>
          </select>
        </Field>
        <div className="flex items-end">
          <button className="btn-primary !py-2.5 text-sm" disabled={busy}>
            <UserPlus size={15} /> {busy ? 'Adding…' : 'Add'}
          </button>
        </div>
        {error && <p className="text-sm text-red-400 sm:col-span-3">{error}</p>}
        {success && <p className="text-sm text-emerald-400 sm:col-span-3">{success}</p>}
        <p className="text-xs text-zinc-500 sm:col-span-3">
          They need an account first (created at <span className="text-zinc-300">/login</span> on the website) — then add
          their email here to grant access.
        </p>
      </form>

      <h2 className="mt-8 font-display font-bold text-white">Current team</h2>
      <div className="mt-4 space-y-3">
        {/* The owner account is always an admin via the security rules */}
        <div className="card flex flex-wrap items-center gap-4 border-accent-500/30 p-4 sm:px-5">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-accent-600 to-sky-500 text-white">
            <Sparkles size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-display font-semibold text-white">{ADMIN_EMAILS[0]}</p>
            <p className="text-xs text-zinc-500">Owner — permanent admin (set in the security rules)</p>
          </div>
          <span className="rounded-full border border-accent-400/40 bg-accent-400/10 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-accent-300">
            Owner
          </span>
        </div>

        {loading ? (
          <EmptyState text="Loading…" />
        ) : items.length === 0 ? (
          <p className="py-4 text-sm text-zinc-500">No additional team members yet.</p>
        ) : (
          items.map((m) => (
            <div key={m.id} className="card flex flex-wrap items-center gap-4 p-4 sm:px-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.06] text-accent-400">
                <ShieldCheck size={18} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-display font-semibold text-white">{m.name || m.email}</p>
                <p className="text-xs text-zinc-500">{m.email}</p>
              </div>
              <select
                className="input !w-auto text-xs"
                value={m.role}
                onChange={(e) => setRole(m, e.target.value as StaffRole)}
              >
                <option value="cleaner">Cleaner</option>
                <option value="admin">Admin</option>
              </select>
              <button className="glass rounded-lg p-2 text-zinc-300 hover:text-red-400" title="Remove" onClick={() => remove(m)}>
                <Trash2 size={15} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
